import { useCallback, useMemo } from 'react';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Typography } from '@mui/material';

import CheckboxMultipleOutline from '@/assets/icons/CheckboxMultipleOutline';
import Button from '@/components/Buttons/Button';
import { ExportTypes } from '@/features/DataHub/modules/Export/types';
import ClearAllButton from '@/components/ClearAllButton';
import { NameInput } from '@/features/Dashboard/components/NameInput';
import { DataHubCard } from '@/features/DataHub/components/DataHubCard';
import { ItemDetails } from '@/features/DataHub/components/ItemDetails';
import PreviewAccordion from '@/features/DataHub/components/Preview/PreviewAccordion';
import { useExport } from '@/features/DataHub/modules/Export/context/ExportContext';
import useSelectionFunctions from '@/features/SymbolSelector/hooks/useSelectionFunctions';
import { useAppDispatch } from '@/hooks/redux-hooks';
import { closeDrawer } from '@/slices/drawerSlice';

const Buttons = () => {
  const dispatch = useAppDispatch();

  const handleBackToSelector = () => {
    dispatch(closeDrawer());
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '8px',
        fontSize: '14px',
        color: 'text.primary'
      }}
    >
      <Button
        sx={{
          padding: '8px 16px',
          backgroundColor: 'gx.actionBarButtonTile'
        }}
        variant='text'
        onClick={handleBackToSelector}
      >
        <Box
          sx={{
            width: '16px',
            height: '16px',
            mr: 1,
            backgroundColor: 'primary.main'
          }}
        />{' '}
        Back to selector
      </Button>
    </Box>
  );
};

const ItemDetailsSection = () => {
  const { uniqueSymbols, exportName, setExportName, exportSettings } = useExport();
  const { removeSymbolsFromSelection } = useSelectionFunctions();
  const symbols = useMemo(() => Array.from(uniqueSymbols), [uniqueSymbols]);
  const handleClearAllClick = useCallback(() => {
    removeSymbolsFromSelection(symbols);
  }, [removeSymbolsFromSelection, symbols]);

  return (
    <DataHubCard
      expandable={false}
      renderRight={<Buttons />}
      title={
        <>
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              minWidth: '100px',
              width: `${Math.max((exportName?.length ?? 0) + 0.5, 10)}ch`,
              '& .name-input': {
                flex: 'none',
                width: '100%'
              }
            }}
          >
            <NameInput
              isEmpty={exportName === ''}
              maxLength={25}
              value={exportName ?? undefined}
              onChange={value => {
                setExportName(value);
              }}
            />
          </Box>
          <EditOutlinedIcon sx={{ fontSize: '16px' }} />
        </>
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontWeight: 700
            }}
          >
            <CheckboxMultipleOutline /> {symbols.length} item{symbols.length === 1 ? '' : 's'}{' '}
            selected
          </Typography>
          <ClearAllButton onClick={handleClearAllClick} />
        </Box>
        <ItemDetails symbols={symbols} />
        {exportSettings.exportType === ExportTypes.DATA && <PreviewAccordion />}
      </Box>
    </DataHubCard>
  );
};

export default ItemDetailsSection;
